<script setup>
import {ref, computed, watch, onMounted} from 'vue';
import {useRoute} from 'vue-router';
import moment from 'moment';
import CountryChoice from "@/components/CountryChoice.vue";
import WatchLinkCell from "@/views/projects/watch_links/WatchLinkCell.vue";
import HTTP from "@/api/common";
import {LinkApi, ProjectApi} from "@/api/project";
import {WatchLinkApi} from "@/api/watch_links";
import {notify} from "@kyvg/vue3-notification";

const route = useRoute();

const table = ref([]);
const engine = ref("yandex");
const custom_engines = ["yandex", "google"];
const project = ref(null);
const active_country = ref(null);
const all_count = ref(0);
const size = ref(50);
const page = ref(1);
const search = ref('');
const allowedDates = ref([]);
const end_date = ref(moment().format("YYYY-MM-DD"));
const start_date = ref(moment().add(-7, "days").format("YYYY-MM-DD"));

const keywords = computed(() => {
  return project.value?.keywords.filter(x =>
      !x.countries.length ||
      x.countries.find(c => c.code === active_country.value.code)
  );
});
const engines = computed(() => {
  return custom_engines.filter(x => active_country.value ? active_country.value[x] : false)
});

const pager = computed(() => {
  let page_count = Math.ceil(all_count.value / size.value);
  let pages = Array.from({length: page_count}, (_, i) => i + 1);
  let array = [];

  if (page.value > 5) {
    array = array.concat(pages.slice(0, page.value - 3 > 3 ? 3 : page.value - 3));
    array.push('...');
  }

  array = array.concat(pages.slice(
      page.value - 4 > 0 ? page.value - 4 : 0,
      page.value + 3
  ));

  if (page.value < (pages.length - 5)) {
    array.push('...');
    array = array.concat(pages.slice(pages.length - 3, pages.length));
  }

  return array;
});

watch(page, () => {
  getData();
});

function setDatesFromAllowedDates() {
  if (allowedDates.value.length > 0) {
    end_date.value = allowedDates.value[allowedDates.value.length - 1];
    if (allowedDates.value.length >= 7) {
      start_date.value = allowedDates.value[allowedDates.value.length - 7];
    } else {
      start_date.value = allowedDates.value[0];
    }
  } else {
    end_date.value = moment().format("YYYY-MM-DD");
    start_date.value = moment().add(-7, "days").format("YYYY-MM-DD");
  }
}

onMounted(() => {
  HTTP.get(`planfix/target/get_project/?project_id=${route.params.id}`).then((response) => {
    project.value = response.data;
    active_country.value = project.value.countries[0];

    end_date.value = moment(project.value.last_parser).format("YYYY-MM-DD");
    start_date.value = moment(end_date.value).add(-7, "days").format("YYYY-MM-DD");

    ProjectApi.get_avail_date_calendar(route.params.id).then(response => {
      allowedDates.value = response;
      getData();
    });
  });
});

function changeCountry(country) {
  if (active_country.value.code !== country.code) {
    active_country.value = country;
    engine.value = engines.value[0]
    getData();
  }
}

function getData() {
  if (!active_country.value) return;

  const data = {
    country_code: active_country.value.code,
    project_id: route.params.id,
    page: page.value,
    size: size.value,
    search: search.value,
    start_date: start_date.value,
    end_date: end_date.value,
  };

  const query = new URLSearchParams(data).toString();
  WatchLinkApi.list(query).then((response) => {
    table.value = response.results;
    all_count.value = response.count;
  }).catch(error => {
    console.error("Error fetching data:", error);
  });
}

function getReport() {
  let filters = {
    country_code: active_country.value.code,
    project_id: route.params.id,
    search: search.value,
    start_date: start_date.value,
    end_date: end_date.value,
  };
  let f_string = new URLSearchParams(filters).toString();
  WatchLinkApi.get_report_data(f_string).then(() => {
    notify({
      type: 'success',
      text: 'Процесс создания отчёта запущен. Отчёт будет доступен в разде "Отчёты"'
    });
  })
}

function saveComment(data) {
  LinkApi.update(data, route.params.id).then(() => {
    notify({
      type: 'success',
      text: 'Данные обновлены'
    });
  })
}

const removeLinkWatch = (link_id, index) => {
  WatchLinkApi.remove({
    link_id: link_id,
    project_id: route.params.id
  }).then(() => {
    table.value.splice(index, 1);
    notify({
      type: 'success',
      text: 'Ссылка удалена из отслеживания'
    });
  });
};

</script>
<template>
  <main class="dashboard links">
    <div class="mb-4">
      <h2>Отслеживаемые ссылки: {{ project?.name }}</h2>
      <BackButton></BackButton>
    </div>
    <div class="filters mb-5 d-flex">
      <div role="search">
        <i class="bi bi-search"></i>
        <input type="search" class="form-control" placeholder="Поиск по ссылке" v-model="search" aria-label="Поиск">
      </div>
      <div class="dropdown" style="z-index: 1100">
        <button class="btn btn-outline-dark dropdown-toggle" type="button" data-bs-toggle="dropdown"
                aria-expanded="false">
          <i :class="engine"></i> {{ engine }}
        </button>
        <ul class="dropdown-menu">
          <li v-for="eng in engines"><a class="dropdown-item" @click.prevent="engine=eng" href="#"><i :class="eng"></i>{{
              eng
            }}</a>
          </li>
        </ul>
      </div>

      <CountryChoice v-if="active_country"
                     v-bind:active_country.sync="active_country"
                     v-bind:project_id="$route.params.id"
                     v-bind:countries="project.countries"
                     v-bind:changeCountry="changeCountry">
      </CountryChoice>
      <date-picker v-model="start_date"
                   style="width: 200px"
                   locale="ru"
                   :clearable="false"
                   format="dd.MM.yyyy"
                   model-type="yyyy-MM-dd"
                   auto-apply
                   :allowed-dates="allowedDates"
                   :enable-time-picker="false"></date-picker>
      <date-picker v-model="end_date"
                   style="width: 200px"
                   locale="ru"
                   :clearable="false"
                   format="dd.MM.yyyy"
                   model-type="yyyy-MM-dd"
                   auto-apply
                   :allowed-dates="allowedDates"
                   :enable-time-picker="false"></date-picker>

      <button class="btn btn-dark btn-sm" @click="getData();">Показать</button>
      <button class="btn btn-sm btn-dark" @click="getReport()" title="Выгрузить отчёт">
        <i class="bi bi-file-arrow-down"></i>
      </button>
    </div>

    <div class="table-responsive" style="height: 100%">
      <table class="table">
        <thead>
        <tr class="sticky-top">
          <th colspan="3"></th>
          <th v-for="keyword in keywords" colspan="2">{{ keyword.name }}</th>
        </tr>
        <tr class="sticky-top">
          <th rowspan="2">№</th>
          <th rowspan="2">URL</th>
          <th rowspan="2">Комментарий</th>
          <template v-for="keyword in keywords">
            <th colspan="2" v-for="engine in engines.filter(x => x===engine)">{{ engine }}</th>
          </template>
        </tr>
        <tr class="sticky-top">

          <template v-for="keyword in keywords">
            <template v-for="engine in engines.filter(x => x===engine)">
              <th>{{ $moment(start_date).format("DD.MM") }}</th>
              <th>{{ $moment(end_date).format("DD.MM") }}</th>
            </template>
          </template>
        </tr>

        </thead>
        <tbody>
        <template v-for="(link, index) in table">
          <tr>
            <td>{{ index + 1 + (page - 1) * size }}</td>
            <td class="link">
              <a :style="$func.getClass(link)" style="width: 500px; display: block; word-break: break-all;"
                   target="_blank" :href="link.link">{{ link.link }}</a>
                <button class="btn btn-sm btn-outline-secondary" @click="removeLinkWatch(link.id, index)" title="Удалить из отслеживания"><i class="bi bi-trash"></i></button>
            </td>
            <td>
              <div class="comment">
                <popper hover>
                  <span>{{ (link.comment || "").substring(0, 50) }}...</span>
                  <template #content>
                     <span class="popper text-start">
                        <span>{{ link.comment }}</span>
                    </span>
                  </template>
                </popper>
                <button class="btn btn-sm text-dark" @click="$modal('show', 'comment_'+link.id);"><i
                    class="bi bi-pencil-fill"></i></button>
              </div>
              <modal :modal_id="'comment_'+link.id">
                <template v-slot:header>
                  <h4>Комментарий</h4>
                </template>
                <template v-slot:body>
                  <textarea style="height: 150px" v-model="link.comment" class="form-control"></textarea>
                </template>
                <template v-slot:footer>
                  <button class="btn btn-sm btn-secondary" @click="$modal('hide', 'comment_'+link.id);">Отмена</button>
                  <button class="btn btn-sm btn-dark" @click="saveComment({id: link.id, comment: link.comment})">
                    Сохранить
                  </button>
                </template>
              </modal>
            </td>
            <template v-for="keyword in keywords">
              <template v-for="engine in engines.filter(x => x===engine)">
                <WatchLinkCell
                    :start="link.searches.results.find(x => x.keyword_id == keyword.id && x.engine == engine && x.date == start_date)?.place||0"
                    :end="link.searches.results.find(x => x.keyword_id == keyword.id && x.engine == engine && x.date == end_date)?.place||0"></WatchLinkCell>
              </template>
            </template>
          </tr>
        </template>
        </tbody>
      </table>
      <nav>
        <ul class="pagination justify-content-center fz-9" v-if="pager.length">
          <li :class="`page-item${p===page?' active':''}`" v-for="p in pager">
            <a v-if="typeof p === 'number'" class="page-link" v-on:click.prevent="page=p" href="">{{ p }}</a>
            <span class="page-link" v-else>{{ p }}</span>
          </li>
        </ul>
      </nav>
    </div>
  </main>
</template>

<style scoped lang="scss">
td, th {
  border-right: 1px solid #cecece;
  border-bottom: 1px dotted #cecece;
}

td {
  position: relative;

  .comment {
    span {
      cursor: pointer;
    }

    button {
      position: absolute;
      right: 0;
      top: 0;
    }
  }
  &.link{
    position: relative;
    button{
      display: none;
      position: absolute;
      right: 2px;
       top: 50%;
    transform: translate(0, -50%);
    }
    &:hover{
      button{
        display: block;
      }
    }
  }
}


th {
  text-align: center;
}


.place {
  width: 40px;
  text-align: left;
  margin-left: 0;
  flex: 0 0 40px;
}

</style>
